import classNames from 'classnames';
import { useState } from 'react';
import styled from 'styled-components';

import { FadeInOut } from 'atoms/animations/FadeInOut';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelS, LabelXS } from 'atoms/typography';
import { useIntlContext } from 'contexts/intl';
import { useSportContext } from 'contexts/sport';
import { useWalletDrawerContext } from 'contexts/walletDrawer';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { laptopAndAbove } from 'style/mediaQuery';

import { MarketCredits } from './MarketCredits';
import { MobileUserCurrenciesDialog } from './MobileUserCurrenciesDialog';
import { MoneyBalances } from './MoneyBalances';
import type { InGameCurrencyAgreggated } from './useUserCurrencies';
import { useUserCurrencies } from './useUserCurrencies';

const Root = styled(Vertical)`
  position: relative;
  width: 100%;
`;
const InGameCurrenciesWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  & > :not(:first-child) {
    margin-left: calc(-1 * var(--double-unit));
  }

  @media ${laptopAndAbove} {
    .sidebarCollapsed & {
      flex-direction: column;
      & > * {
        width: 100%;
      }
    }

    & > :not(:first-child) {
      margin-left: 0;
    }
  }
`;
const InGameCurrencyWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  background-color: var(--c-black);
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  border: 1px solid var(--c-nd-150);
  padding: 0 var(--unit);

  &.expanded {
    border-radius: var(--double-unit);
    width: auto;
  }

  &.selected {
    background-color: var(--c-nd-50);
    border-color: var(--c-nd-300);
  }
  transition:
    300ms background-color ease-in-out,
    300ms border-color ease-in-out;

  @media ${laptopAndAbove} {
    flex-direction: column;
    justify-content: center;

    flex: 1;
    padding: var(--unit);
    border-radius: var(--unit);
    height: auto;
  }
`;

type Props = {
  expandedCurrencies?: InGameCurrencyAgreggated[];
  sidebarCollapsed?: boolean;
};
export const UserCurrencies = ({
  expandedCurrencies,
  sidebarCollapsed,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const { showDrawer } = useWalletDrawerContext();
  const isLaptopAndAbove = useIsLaptop();
  const { sport } = useSportContext();
  const [mobileDialogOpened, setMobileDialogOpened] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] =
    useState<InGameCurrencyAgreggated | null>(null);

  const currencies = useUserCurrencies(expandedCurrencies);

  const selectedCurrency = currencies.find(
    currency => currency.id === selectedCurrencyId
  );

  const AmountLabel = sidebarCollapsed ? LabelXS : LabelS;

  return (
    <>
      <MobileUserCurrenciesDialog
        open={mobileDialogOpened}
        onClose={() => setMobileDialogOpened(false)}
        currencies={currencies}
      />
      <Root
        as={isLaptopAndAbove ? 'div' : 'button'}
        onClick={
          isLaptopAndAbove ? undefined : () => setMobileDialogOpened(true)
        }
      >
        <Vertical onMouseLeave={() => setSelectedCurrencyId(null)}>
          {!!isLaptopAndAbove && !sidebarCollapsed && (
            <FadeInOut>{selectedCurrency?.details?.({})}</FadeInOut>
          )}
          <InGameCurrenciesWrapper>
            {currencies.map((currency, i) => {
              const { id, icon, expanded, visibleOnSports, amount, onClick } =
                currency;

              return (
                visibleOnSports.includes(sport) &&
                typeof amount === 'number' && (
                  <InGameCurrencyWrapper
                    key={id}
                    as={isLaptopAndAbove ? 'button' : 'div'}
                    onClick={isLaptopAndAbove ? onClick : undefined}
                    className={classNames({
                      expanded: !isLaptopAndAbove && expanded,
                      selected: currency.id === selectedCurrency?.id,
                    })}
                    style={{ zIndex: currencies.length - i }}
                    onMouseEnter={() => setSelectedCurrencyId(currency.id)}
                  >
                    {icon}
                    {!!(isLaptopAndAbove || expanded) && (
                      <AmountLabel bold>
                        {formatNumber(amount, {
                          notation: 'compact',
                          compactDisplay: 'short',
                        })}
                      </AmountLabel>
                    )}
                  </InGameCurrencyWrapper>
                )
              );
            })}
          </InGameCurrenciesWrapper>
        </Vertical>
        {!!isLaptopAndAbove && (
          <>
            <MoneyBalances
              onClick={showDrawer}
              sidebarCollapsed={sidebarCollapsed}
            />
            <button onClick={showDrawer} type="button">
              <MarketCredits
                sport={sport}
                sidebarCollapsed={sidebarCollapsed}
              />
            </button>
          </>
        )}
      </Root>
    </>
  );
};
